import { decodeJwtParts } from './debug/jwtDebug';
import { normalizeEnvKey } from './envKeyUtils';
import { getValidAccessToken } from './getValidAccessToken';

const GUEST_SESSION_KEY = 'zoomedin_guest_session_id';

const getFunctionsUrl = (slug) => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${slug}`;
};

const buildHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 && !jwtAnonDecoded?.decodeError && jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 && !anonDecoded?.decodeError && anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt ? jwtAnonRaw : anonLooksJwt ? anonRaw : null;
  const accessToken = await getValidAccessToken();

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (apikeyToSend) {
    headers.apikey = apikeyToSend;
  }
  return headers;
};

const getOrCreateGuestSessionId = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  const existing = window.localStorage.getItem(GUEST_SESSION_KEY);
  if (existing && typeof existing === 'string') {
    return existing;
  }

  let nextId = null;
  if (window.crypto?.randomUUID) {
    nextId = window.crypto.randomUUID();
  } else {
    nextId = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  try {
    window.localStorage.setItem(GUEST_SESSION_KEY, nextId);
  } catch {
    // Ignore storage failures; request still proceeds without persisted session.
  }
  return nextId;
};

const getRateLimitMessage = (retryAfterSeconds) => {
  const seconds = Number(retryAfterSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'Too many reports submitted. Please try again later.';
  }
  if (seconds < 60) {
    return `Too many reports submitted. Try again in ${Math.ceil(seconds)} seconds.`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Too many reports submitted. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

export const submitGameIssueReport = async ({
  gameType = 'unlimited',
  roundId = null,
  reason,
  impactedResult = false,
  note = '',
  word = '',
  imageSource = null,
  imageId = null,
  imageUrl = '',
}) => {
  const headers = await buildHeaders();
  const guestSessionId = getOrCreateGuestSessionId();

  const payload = {
    gameType,
    roundId,
    reason,
    impactedResult: Boolean(impactedResult),
    note,
    word,
    imageSource,
    imageId,
    imageUrl,
    guestSessionId,
  };

  const doRequest = async (requestHeaders) =>
    await fetch(getFunctionsUrl('game-feedback'), {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(payload),
    });

  let response = await doRequest(headers);

  if (response.status === 401 && headers.Authorization) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      response = await doRequest(retryHeaders);
    }
  }

  const responseText = await response.text();
  let dataJson = null;
  try {
    dataJson = responseText ? JSON.parse(responseText) : null;
  } catch {
    dataJson = null;
  }

  if (!response.ok) {
    const error = new Error(dataJson?.error || responseText || 'Failed to submit report.');
    error.code = dataJson?.code;
    error.status = response.status;
    error.retryAfterSeconds = dataJson?.retry_after_seconds ?? null;
    if (error.code === 'RATE_LIMITED') {
      error.userMessage = getRateLimitMessage(error.retryAfterSeconds);
    }
    throw error;
  }

  return dataJson;
};

